import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, signal } from '@angular/core';

import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ButtonModule } from 'primeng/button';
import { HttpErrorResponse } from '@angular/common/http';
import { BillingService } from '@generated/services/billing.service';
import { PlanCard } from '../plan-card/plan-card';
import { NotificationService } from '@core/services/notification.service';
import { Subscription as SubscriptionService } from '@core/services/subscription';
import type { PlanDto } from '@generated/models/plan-dto';
import type { CreateOrUpdateSubscriptionResult } from '@generated/models/create-or-update-subscription-result';

@Component({
  selector: 'app-plans',
  imports: [ProgressSpinnerModule, PlanCard, ConfirmDialogModule, ButtonModule],
  templateUrl: './plans.html',
  styleUrl: './plans.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService]
})
export class Plans implements OnInit {
  plans = signal<PlanDto[]>([]);
  isLoading = signal(false);
  isChangingSubscription = signal(false);

  constructor(
    private billingService: BillingService,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
    public subscriptionService: SubscriptionService,
    public confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadPlans();
  }

  private loadPlans(): void {
    this.isLoading.set(true);
    this.cdr.markForCheck();

    this.billingService.apiBillingPlansGet().subscribe({
      next: (result) => {
        const sortedPlans = (result.plans || []).sort((a, b) => {
          const firstNonZeroA = this.getFirstNonZeroPrice(a);
          const firstNonZeroB = this.getFirstNonZeroPrice(b);
          return firstNonZeroA - firstNonZeroB;
        });
        this.plans.set(sortedPlans);
        this.isLoading.set(false);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to load plans:', error);
        this.isLoading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  private getFirstNonZeroPrice(plan: PlanDto): number {
    if (!plan.charges || plan.charges.length === 0) {
      return 0;
    }

    for (const charge of plan.charges) {
      const ranges = charge.properties?.graduated_ranges;
      if (ranges && ranges.length > 0) {
        for (const range of ranges) {
          const perUnitAmount = parseFloat(range.per_unit_amount || '0');
          if (perUnitAmount > 0) {
            return perUnitAmount;
          }
        }
      }
    }

    return 0;
  }

  isCurrentPlan(plan: PlanDto): boolean {
    return this.subscriptionService.data()?.subscription?.plan_code === plan.code;
  }

  onSubscribe(plan: PlanDto): void {
    if (!plan.code) {
      console.error('Plan code is missing');
      return;
    }

    const planName = plan.name ?? '';
    this.confirmationService.confirm({
      message: $localize`:@@plans.subscribe.confirmMessage:Are you sure you want to change your subscription to ${planName}:planName:?`,
      header: $localize`:@@plans.subscribe.confirmHeader:Confirm Subscription Change`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: $localize`:@@common.yes:Yes`,
      rejectLabel: $localize`:@@common.cancel:Cancel`,
      closable: false,
      closeOnEscape: false,
      accept: () => {
        this.changeSubscription(plan.code!);
      }
    });
  }

  onCancel(plan: PlanDto): void {
    this.confirmationService.confirm({
      message: $localize`:@@plans.cancel.confirmMessage:You will receive a final invoice and be transitioned to the Free Plan. Are you sure you want to cancel your subscription?`,
      header: $localize`:@@plans.cancel.confirmHeader:Cancel Subscription`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: $localize`:@@plans.cancel.yesCancel:Yes, Cancel`,
      rejectLabel: $localize`:@@common.no:No`,
      closable: false,
      closeOnEscape: false,
      accept: () => {
        this.cancelSubscription();
      }
    });
  }

  private changeSubscription(planCode: string): void {
    this.isChangingSubscription.set(true);
    this.cdr.markForCheck();

    this.billingService.apiBillingSubscriptionChangePost({
      body: { lago_plan_code: planCode }
    }).subscribe({
      next: (result) => {
        if (result.checkout_url) {
          window.location.href = result.checkout_url;
        } else if (result.success) {
          this.subscriptionService.loadSubscription();
          this.isChangingSubscription.set(false);
          this.confirmationService.close();
          this.cdr.markForCheck();
        } else if (result.error_message) {
          console.error('Subscription change failed:', result.error_message);
          this.isChangingSubscription.set(false);
          this.confirmationService.close();
          this.cdr.markForCheck();
        }
      },
      error: (error: HttpErrorResponse) => {
        console.error('Failed to change subscription:', error);

        let errorMessage = $localize`:@@plans.notify.changeFail:Failed to change subscription`;

        if (error.status === 400 && error.error) {
          const errorResult = error.error as CreateOrUpdateSubscriptionResult;
          if (errorResult.error_message) {
            errorMessage = errorResult.error_message;
          }
        }

        this.notificationService.showError(errorMessage);
        this.isChangingSubscription.set(false);
        this.confirmationService.close();
        this.cdr.markForCheck();
      }
    });
  }

  private cancelSubscription(): void {
    this.isChangingSubscription.set(true);
    this.cdr.markForCheck();

    this.billingService.apiBillingSubscriptionCancelPost().subscribe({
      next: () => {
        this.subscriptionService.loadSubscription();
        this.isChangingSubscription.set(false);
        this.confirmationService.close();
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Failed to cancel subscription:', error);
        this.isChangingSubscription.set(false);
        this.confirmationService.close();
        this.cdr.markForCheck();
      }
    });
  }
}
